<template>
    <div>
        <div>
            <b-row>
                <b-col cols="12">
                    <SearchComponent v-model="storeFilter" :label="`Store`" :search-u-r-l="`/Store`" :prepopulate="true"
                        :clearable="true" @input="storeFilterChanged" />
                </b-col>
            </b-row>
            <b-row v-if="cards">
                <b-col>
                    <checkbox v-model="pharmacistCreatedErrors" @change="loadErrors">Only My Submitted Errors</checkbox>
                </b-col>
            </b-row>
            <b-row v-if="!hideStatusFilter">
                <b-col class="d-flex flex-wrap" style="gap: 15px;">
                    <checkbox v-model="filterCreated">Created</checkbox>
                    <checkbox v-model="filterResolved">Resolved</checkbox>
                    <checkbox v-model="filterAccepted">Accepted</checkbox>
                    <checkbox v-model="filterAwaitingResponse">Awaiting Response</checkbox>
                    <checkbox v-model="pharmacistAssignedErrors" @change="loadErrors">Only Assigned to Me</checkbox>
                </b-col>
            </b-row>
        </div>
        <hr>
        <!-- Table mode for technicians -->
        <b-table ref="errorsList" v-if="!cards" :items="errors" :fields="fields"
            show-empty :empty-text="'No ' + dispenseErrorLabel + 's found.'"
            striped responsive small class="table-shopping">
            <template #cell(created)="data">
                {{ data.item.created | formatDate('L LT') }}
            </template>
            <template #cell(updated)="data">
                {{ data.item.updated | formatDate('L LT') }}
            </template>
            <template #cell(status)="data">
                <b-badge :variant="statusStyle(data.item.statusEnum)">
                    {{ data.item.status }}
                </b-badge>
            </template>
            <template #cell(actions)="data">
                <router-link
                    v-if="data.item.statusEnum == statusEnums.Created || data.item.statusEnum == statusEnums.AwaitingResponse"
                    :to="{
                        name: 'PrescriptionDetailsId'
                        , params: { rxId: data.item.rxID, dispenseErrorId: data.item.id }
                    }">
                    <i class="now-ui-icons ui-1_zoom-bold" v-b-tooltip.hover title="Check and Resolve"></i>
                </router-link>
                <a href="#" @click.prevent="openErrorDispenseModal(data.item.id)"
                    v-if="data.item.statusEnum == statusEnums.Resolved">
                    <i class="now-ui-icons ui-1_check" v-b-tooltip.hover title="Check and Approve"></i>
                </a>
            </template>
        </b-table>

        <!-- CARDS MODE for pharmacist -->
        <div v-else>
            <b-row v-if="errors.length">
                <b-col cols="3" v-for="(err, index) in errors" :key="err.id">
                    <DispensingErrorCardComponent v-model="errors[index]" @onClickDetails="openErrorDispenseModal" />
                </b-col>
            </b-row>
            <div v-else class="text-center p-4">
                <b>No {{ dispenseErrorLabel + "s" }} found that are pending to approve.</b>
            </div>
        </div>

        <DispensingErrorModalComponent v-model="selectedDispenseError" :review-mode="true" :show-buttons="false"
            @changed-type="loadErrors" />
    </div>
</template>

<script lang="ts">
import Axios from 'axios';
import { BvTableFieldArray } from "bootstrap-vue";
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { DispenseError } from '@/models/DispenseError';
import { Store } from '@/models/Store';

import DispensingErrorModalComponent from '@/components/DispensingError/DispensingErrorModalComponent.vue';
import DispensingErrorCardComponent from '@/components/DispensingError/DispensingErrorCardComponent.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import DispenseErrorStatusEnum = DispenseError.DispenseErrorStatusEnum;
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { getUserSetting, saveUserSetting } from "@/services/UserSettingsService";

@Component({
    name: "DispenseErrorListComponent",
    components: {
        DispensingErrorModalComponent, DispensingErrorCardComponent,
        SearchComponent, Checkbox
    }
})
export default class DispenseErrorListComponent extends Vue {
    @Prop({ default: false, type: Boolean }) private cards!: boolean;
    @Prop({ default: false, type: Boolean }) private hideStatusFilter!: boolean;
    @Prop({ default: "default", type: String }) private mode!: string;

    private statusEnums = DispenseError.DispenseErrorStatusEnum;
    private errors: Array<DispenseError> = [];
    private selectedDispenseError: DispenseError = new DispenseError();
    private storeFilter: Store = new Store();
    private pharmacistCreatedErrors = false;
    private pharmacistAssignedErrors = false;
    private filterCreated = true;
    private filterAccepted = false;
    private filterResolved = false;
    private filterAwaitingResponse = true;

    private fields: BvTableFieldArray = [
        { key: 'created', label: 'Date' },
        { key: 'createdBy', label: 'Reported By' },
        { key: 'rxID', label: 'RxID' },
        { key: 'reason', label: 'Reason' },
        { key: 'errorTypeDesc', label: 'Error Type', sortable: true },
        { key: 'userNameAssignedTo', label: 'Assigned To', sortable: true },
        { key: 'productNameShort', label: 'Drug' },
        { key: 'updated', label: 'Updated' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: '' }
    ];

    created() {
        this.storeFilter.id = this.persistentStoreFilterId || 0;

        this.handleMode();
        this.fetchDispenseErrorLabel();
    }

    @Watch("mode")
    handleMode() {
        this.filterCreated = true;
        this.filterAccepted = false;
        this.filterResolved = false;
        this.filterAwaitingResponse = true;

        if (this.mode === "pending") {
            this.filterResolved = true;
        } else if (this.mode === "assigned") {
            this.pharmacistAssignedErrors = true;
        }

        this.loadErrors();
    }

    @Watch("strStatusFilter")
    async loadErrors() {

        try {
            const response = await Axios.get("DispenseError", {
                params: {
                    statusFilter: this.strStatusFilter,
                    storeID: this.storeFilter?.id
                }
            });

            this.errors = response.data.map((d: DispenseError) => Object.assign(new DispenseError(), d));

            if (this.errors?.length)
                this.errors = this.errors.sort(this.compareFunc);

            if (this.pharmacistCreatedErrors) {
                this.errors = this.errors.filter(e => e.createdBy?.toLowerCase() === e.viewedBy?.toLowerCase())
            }

            if (this.pharmacistAssignedErrors) {
                this.errors = this.errors.filter(e => e.assignedTo?.toLowerCase() == e.viewedBy?.toLowerCase())
            }
        } catch (error) {
            console.error("Error while listing errors.", { error, response: (error as any)?.response });
            this.$notification(NotificationOptions.error(error));
        }
    }

    private compareFunc = (a: DispenseError, b: DispenseError): number => {
        const compStatus = this.statusPriority(a.statusEnum) - this.statusPriority(b.statusEnum);
        if (compStatus) return compStatus;

        const dateA = new Date(a.created);
        const dateB = new Date(b.created);
        return dateA.getTime() - dateB.getTime();
    }

    statusPriority(statusNbr: DispenseError.DispenseErrorStatusEnum): number {
        if (statusNbr == DispenseError.DispenseErrorStatusEnum.Resolved) return 0;
        if (statusNbr == DispenseError.DispenseErrorStatusEnum.Created) return 1;
        if (statusNbr == DispenseError.DispenseErrorStatusEnum.Accepted) return 2;
        if (statusNbr == DispenseError.DispenseErrorStatusEnum.AwaitingResponse) return 3;
        return 100;
    }

    get persistentStoreFilterId(): number | null {
        const val = Number(getUserSetting('dispenseError_storeFilterId'));
        return val;
    }

    set persistentStoreFilterId(storeId: number | null) {
        saveUserSetting('dispenseError_storeFilterId', (storeId || 0).toString());
    }

    get strStatusFilter(): string {
        let res = '';
        if (this.selectedStatusFilter?.length)
            res = this.selectedStatusFilter.map(f => Number(f).toString()).reduce((f1, f2) => f1 + ',' + f2)
        return res;
    }

    get selectedStatusFilter(): Array<DispenseError.DispenseErrorStatusEnum | null> {
        const res: Array<DispenseError.DispenseErrorStatusEnum | null> = [
            this.filterCreated ? DispenseError.DispenseErrorStatusEnum.Created : null
            , this.filterResolved ? DispenseError.DispenseErrorStatusEnum.Resolved : null
            , this.filterAccepted ? DispenseError.DispenseErrorStatusEnum.Accepted : null
            , this.filterAwaitingResponse ? DispenseError.DispenseErrorStatusEnum.AwaitingResponse : null
        ].filter(st => st != null);

        return res;
    }

    openErrorDispenseModal(dispenseErrorId: number) {
        // Load Error
        Axios.get(`DispenseError/${dispenseErrorId}`)
            .then(response => {
                this.selectedDispenseError = response.data;
                //Open modal with error details
                this.$bvModal.show("dispense-error-modal");
            })
            .catch(err => {
                console.error("Error while getting error details", err);
                this.$notification(NotificationOptions.error(err));
            })

    }

    statusStyle(status: DispenseError.DispenseErrorStatusEnum) {
        switch (status) {
            case DispenseErrorStatusEnum.Accepted: return "info";
            case DispenseErrorStatusEnum.Resolved: return "success";
            case DispenseErrorStatusEnum.AwaitingResponse: return "danger";
            default: return "warning";
        }
    }

    storeFilterChanged(store: Store) {
        this.persistentStoreFilterId = Number(store?.id);
        this.loadErrors();
    }

    protected dispenseErrorLabel = "";

    async fetchDispenseErrorLabel() {
        try {
            const resp = await this.$http.get("/Configuration/DispenseErrorLabel");
            this.dispenseErrorLabel = resp.data?.trim();
        } catch {
            this.dispenseErrorLabel = "Error";
            //ignore
        }
    }
}

</script>
